import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { LineChart as ReChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { Card, Btn, Empty } from "../../components/UI";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { FLabel, FInput, FSelect } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { Plus, Activity, Pencil, Trash2, TrendingUp } from "lucide-react";

const EMPTY = {
  date: dayjs().format("YYYY-MM-DD"), time: dayjs().format("HH:mm"),
  bp_systolic: "", bp_diastolic: "", pulse: "", pulse_pattern: "Regular",
  respiration: "", respiration_pattern: "Regular", temperature: "", temperature_method: "Oral",
  weight_lbs: "", height_in: "", o2_saturation: "", pain_level: 0, comments: "",
};

const CHART_METRICS = [
  { key: "bp_systolic", label: "BP (Systolic)", unit: "mmHg", ref_high: 130, ref_low: 90, color: "#B26A00" },
  { key: "bp_diastolic", label: "BP (Diastolic)", unit: "mmHg", ref_high: 80, ref_low: 60, color: "#4E342E" },
  { key: "pulse", label: "Pulse", unit: "bpm", ref_high: 100, ref_low: 60, color: "#4E342E" },
  { key: "temperature", label: "Temperature", unit: "F", ref_high: 100.4, ref_low: 97.0, color: "#B26A00" },
  { key: "o2_saturation", label: "SpO2", unit: "%", ref_high: null, ref_low: 95, color: "#4E342E" },
  { key: "weight_lbs", label: "Weight", unit: "lbs", ref_high: null, ref_low: null, color: "#4E342E" },
  { key: "bmi", label: "BMI", unit: "kg/m2", ref_high: 25, ref_low: 18.5, color: "#B26A00" },
  { key: "pain_level", label: "Pain Level", unit: "0-10", ref_high: null, ref_low: null, color: "#B26A00" },
];

export default function VitalSigns({ client }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const [items, setItems] = useState([]);
  const [trends, setTrends] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [panel, setPanel] = useState(false);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [showCharts, setShowCharts] = useState(true);

  const load = () => {
    api.get(`/clients/${client.id}/vitals`).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/clients/${client.id}/vitals-trends`).then((r) => setTrends(r.data?.trends || {})).catch(() => {});
  };
  useEffect(() => { if (client?.id) load(); }, [client?.id]); // eslint-disable-line

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const openAdd = () => { setForm({ ...EMPTY, date: dayjs().format("YYYY-MM-DD"), time: dayjs().format("HH:mm") }); setEditId(null); setPanel(true); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it }); setEditId(it.id); setPanel(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["bp_systolic", "bp_diastolic", "pulse", "respiration", "temperature", "weight_lbs", "height_in", "o2_saturation", "pain_level"]
      .forEach((k) => { if (payload[k] === "") payload[k] = null; else if (payload[k] != null) payload[k] = Number(payload[k]); });
    try {
      if (editId) await api.put(`/vitals/${editId}`, payload);
      else await api.post(`/clients/${client.id}/vitals`, payload);
      toast.success(editId ? "Vital signs updated" : "Vital signs added");
      setPanel(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await api.delete(`/vitals/${del.id}`); toast.success("Removed"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const latest = items[0];

  return (
    <div data-testid="vital-signs-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading font-bold text-[#3E2723]">Vital Signs</h3>
          <div className="text-[11px] text-[#7B6157]">BP, pulse, respiration, temp, SpO2, weight, height, BMI, and pain.</div>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="outline" onClick={() => setShowCharts((s) => !s)} data-testid="vitals-toggle-charts">
            <TrendingUp size={13} /> {showCharts ? "Hide charts" : "Show charts"}
          </Btn>
          {editable && (
            <Btn variant="outline" data-testid="vitals-add-btn" onClick={openAdd}>
              <Plus size={13} /> Record Vitals
            </Btn>
          )}
        </div>
      </div>

      {latest && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8" data-testid="vitals-latest">
          {[
            { l: "BP", v: latest.bp_systolic && latest.bp_diastolic ? `${latest.bp_systolic}/${latest.bp_diastolic}` : "-", u: "mmHg" },
            { l: "Pulse", v: latest.pulse ?? "-", u: "bpm" },
            { l: "Resp", v: latest.respiration ?? "-", u: "/min" },
            { l: "Temp", v: latest.temperature ?? "-", u: "F" },
            { l: "SpO2", v: latest.o2_saturation ? `${latest.o2_saturation}` : "-", u: "%" },
            { l: "Wt", v: latest.weight_lbs ?? "-", u: "lbs" },
            { l: "BMI", v: latest.bmi ?? "-", u: "kg/m2" },
            { l: "Pain", v: latest.pain_level ?? "-", u: "0-10" },
          ].map((s) => (
            <Card key={s.l} className="p-2">
              <div className="text-[9px] font-bold uppercase text-[#7B6157]">{s.l}</div>
              <div className="font-heading text-lg font-bold text-[#3E2723]">{s.v}</div>
              <div className="text-[9px] text-[#7B6157]">{s.u}</div>
            </Card>
          ))}
        </div>
      )}

      {showCharts && items.length >= 2 && (
        <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="vitals-charts">
          {CHART_METRICS.map((m) => {
            const data = (trends[m.key] || []).map((p) => ({ ...p, dateShort: dayjs(p.date).format("MMM D") }));
            if (data.length < 2) return null;
            return (
              <Card key={m.key} className="p-3">
                <div className="mb-1 flex items-center justify-between">
                  <div className="text-xs font-bold text-[#3E2723]">{m.label}</div>
                  <div className="text-[10px] text-[#7B6157]">{m.unit}</div>
                </div>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <ReChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: "#8D6E63" }} />
                      <YAxis tick={{ fontSize: 10, fill: "#8D6E63" }} width={32} />
                      <Tooltip formatter={(v) => `${v} ${m.unit}`} />
                      {typeof m.ref_high === "number" && <ReferenceLine y={m.ref_high} stroke="#C62828" strokeDasharray="4 4" strokeWidth={1} />}
                      {typeof m.ref_low === "number" && <ReferenceLine y={m.ref_low} stroke="#1565C0" strokeDasharray="4 4" strokeWidth={1} />}
                      <Line type="monotone" dataKey="value" stroke={m.color} strokeWidth={2} dot={{ r: 3, fill: m.color }} />
                    </ReChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {items.length === 0 ? (
        <Empty text="No vital signs recorded yet" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#EFEBE0]">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {["Date", "Time", "BP", "Pulse", "Resp", "Temp", "SpO2", "Wt", "BMI", "Pain", ""].map((h) => (
                  <th key={h} className="bg-[#FDFCF8] border-b border-[#EFEBE0] text-[#4E342E] font-semibold py-2 px-3 text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`vitals-row-${it.id}`} className="hover:bg-[#FDFCF8]">
                  <td className="border-b border-[#EFEBE0] py-2 px-3 tabular-nums">{dayjs(it.date).format("MM/DD/YYYY")}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 tabular-nums">{it.time || "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3 font-mono">{it.bp_systolic && it.bp_diastolic ? `${it.bp_systolic}/${it.bp_diastolic}` : "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.pulse ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.respiration ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.temperature ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.o2_saturation ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.weight_lbs ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.bmi ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">{it.pain_level ?? "-"}</td>
                  <td className="border-b border-[#EFEBE0] py-2 px-3">
                    <div className="flex justify-end gap-1">
                      {editable && (
                        <button type="button" data-testid={`vitals-edit-${it.id}`} onClick={() => openEdit(it)}
                          className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={12} /></button>
                      )}
                      {editable && (
                        <button type="button" data-testid={`vitals-delete-${it.id}`} onClick={() => setDel(it)}
                          className="h-7 w-7 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SlidePanel open={panel} onClose={() => setPanel(false)} title={editId ? "Edit Vital Signs" : "Record Vital Signs"} testId="vitals-slide-panel">
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Date</FLabel><DateField value={form.date} onChange={(v) => setF("date", v)} testId="vitals-field-date" /></div>
            <div><FLabel>Time</FLabel><FInput type="time" value={form.time} onChange={(e) => setF("time", e.target.value)} /></div>
            <div><FLabel>BP Systolic</FLabel><FInput type="number" value={form.bp_systolic} onChange={(e) => setF("bp_systolic", e.target.value)} data-testid="vitals-field-bp-sys" /></div>
            <div><FLabel>BP Diastolic</FLabel><FInput type="number" value={form.bp_diastolic} onChange={(e) => setF("bp_diastolic", e.target.value)} data-testid="vitals-field-bp-dia" /></div>
            <div><FLabel>Pulse (bpm)</FLabel><FInput type="number" value={form.pulse} onChange={(e) => setF("pulse", e.target.value)} data-testid="vitals-field-pulse" /></div>
            <div><FLabel>Pulse Pattern</FLabel><FSelect value={form.pulse_pattern} onChange={(e) => setF("pulse_pattern", e.target.value)}>
              {["Regular", "Irregular", "Weak", "Bounding"].map((o) => <option key={o} value={o}>{o}</option>)}
            </FSelect></div>
            <div><FLabel>Respiration (/min)</FLabel><FInput type="number" value={form.respiration} onChange={(e) => setF("respiration", e.target.value)} /></div>
            <div><FLabel>Resp Pattern</FLabel><FSelect value={form.respiration_pattern} onChange={(e) => setF("respiration_pattern", e.target.value)}>
              {["Regular", "Labored", "Shallow", "Deep"].map((o) => <option key={o} value={o}>{o}</option>)}
            </FSelect></div>
            <div><FLabel>Temperature (F)</FLabel><FInput type="number" step="0.1" value={form.temperature} onChange={(e) => setF("temperature", e.target.value)} data-testid="vitals-field-temp" /></div>
            <div><FLabel>Temp Method</FLabel><FSelect value={form.temperature_method} onChange={(e) => setF("temperature_method", e.target.value)}>
              {["Oral", "Tympanic", "Temporal", "Axillary", "Rectal"].map((o) => <option key={o} value={o}>{o}</option>)}
            </FSelect></div>
            <div><FLabel>Weight (lbs)</FLabel><FInput type="number" step="0.1" value={form.weight_lbs} onChange={(e) => setF("weight_lbs", e.target.value)} data-testid="vitals-field-weight" /></div>
            <div><FLabel>Height (in)</FLabel><FInput type="number" step="0.1" value={form.height_in} onChange={(e) => setF("height_in", e.target.value)} data-testid="vitals-field-height" /></div>
            <div><FLabel>SpO2 (%)</FLabel><FInput type="number" value={form.o2_saturation} onChange={(e) => setF("o2_saturation", e.target.value)} data-testid="vitals-field-spo2" /></div>
            <div><FLabel>Pain (0-10)</FLabel><FInput type="number" min="0" max="10" value={form.pain_level} onChange={(e) => setF("pain_level", e.target.value)} data-testid="vitals-field-pain" /></div>
            <div className="col-span-2"><FLabel>Comments</FLabel><textarea rows={2} value={form.comments} onChange={(e) => setF("comments", e.target.value)}
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" /></div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-3">
            <Btn type="button" variant="ghost" onClick={() => setPanel(false)}>Cancel</Btn>
            <Btn type="submit" data-testid="vitals-save-btn">{editId ? "Save" : "Record"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove}
        title="Delete vital signs" description={del ? `Delete vitals from ${dayjs(del.date).format("MM/DD/YYYY")}?` : ""} />
    </div>
  );
}
